import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer (rendered full-screen as an overlay).
 *
 * History integration (Phase 1.5):
 * - Opening the cart pushes a `/cart` history entry so the URL reflects the cart WITHOUT a
 *   page reload and WITHOUT losing the state of the page behind it.
 * - Pressing the browser back button pops that entry and closes the overlay.
 * - Closing via the close button / Escape / backdrop pops the entry we pushed so the URL and
 *   history stay in sync.
 * - A real navigation to /cart (refresh, shared link, no-JS) is unaffected and renders the
 *   main-cart.liquid fallback.
 *
 * The open/close funnel is driven by DialogOpenEvent / DialogCloseEvent (dispatched by
 * DialogComponent.showDialog / closeDialog), so every entry point — the header trigger,
 * add-to-cart auto-open, Escape, backdrop, and the close button — is handled uniformly.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** Whether we currently have a `/cart` history entry that belongs to this overlay. */
  #cartHistoryActive = false;

  /** Guards against re-popping history when the close was itself triggered by a popstate. */
  #closingFromHistory = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    window.addEventListener('popstate', this.#handlePopState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    window.removeEventListener('popstate', this.#handlePopState);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /** @returns {string} The URL to reflect in the address bar while the overlay is open. */
  #cartUrl() {
    return (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';
  }

  /** Pushes the `/cart` history entry when the overlay opens (once). */
  #handleDialogOpen = () => {
    if (this.#cartHistoryActive) return;

    try {
      history.pushState({ cartDrawerOpen: true }, '', this.#cartUrl());
      this.#cartHistoryActive = true;
    } catch (error) {
      // If pushState is unavailable/blocked, fail open: the overlay still works, just
      // without URL/back-button integration.
      this.#cartHistoryActive = false;
    }
  };

  /**
   * Runs whenever the dialog finishes closing (button / Escape / backdrop / programmatic).
   * If we own an active history entry AND this close did not come from a popstate, pop it.
   */
  #handleDialogClose = () => {
    if (this.#closingFromHistory) return;
    if (!this.#cartHistoryActive) return;

    this.#cartHistoryActive = false;
    history.back();
  };

  /**
   * Back/forward button. If our overlay owns the current entry, close it without pushing or
   * popping again (the entry was already popped by the browser).
   */
  #handlePopState = () => {
    if (!this.#cartHistoryActive) return;

    this.#cartHistoryActive = false;
    this.#closingFromHistory = true;

    Promise.resolve(this.closeDialog()).finally(() => {
      this.#closingFromHistory = false;
    });
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
